/**
 * `trackSignupConversion()` — browser-side half of the Meta signup-conversion
 * pair (server-side half is the backend `sign_up_successful`), consumed by Meta
 * for ad optimization.
 *
 * Fired once per account creation from the `getUser()` choke point (see
 * `@/lib/utils`), when the get-or-create user response reports
 * `is_newly_created`. `getUser()` is the single call every get-or-create entry
 * point funnels through (app-boot context query, onboarding-v2 completion
 * `fetchQuery`, dictation about-you, ...), so firing there covers every signup
 * path — unlike the old `screenHint=signup` web-auth arm, which relied on
 * `sessionStorage` and missed most entry points. It fires at the same moment
 * the backend emits `sign_up_successful`, so the two halves dedupe cleanly in
 * Meta.
 *
 * Invariants:
 *   1. exactly once per signup — the backend create branch returns
 *      `is_newly_created: true` for a single request per user;
 *   2. flag-gated by `op-signup-conversion-event` — the single rollout /
 *      kill-switch AND environment-scoping gate (enabled only in the production
 *      Flagsmith environment, so staging/dev never reach Meta).
 *
 * Routing: Segment (→ Meta) + Datadog only. `amplitude: false` keeps it out of
 * product analytics; Snowplow is a no-op because `signup_conversion` has no
 * Iglu handler registered (see `trackSnowplowEvent`), so the sanctioned
 * `track()` facade — which can't suppress Snowplow explicitly — is safe here.
 * Carries the full browser-only attribution set (UTMs + every ad-network click
 * ID + the Meta `fbp`/`fbc` cookie ids + the original landing URL). The GTM
 * dataLayer push carries the exact same payload as the Segment event, so GTM
 * tags (Google Ads / Meta) see every attribute Segment does.
 *
 * `kindeUserId` is forwarded as the Meta dedup `event_id` (see below).
 */
import { IS_SIGNUP_CONVERSION_EVENT_ENABLED } from '@/features/flags';
import { pushGtmDataLayerEvent } from '@/lib/analytics/pushGtmDataLayerEvent';
import { track } from '@/lib/analytics/track';
import { getEffectiveAuthProvider } from '@/lib/auth/effectiveAuthProvider';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { mapAuthProviderToProvider } from '@/models/analytics/features/user';
import { getLandingUrl, getMetaCookieIds, getUtmParams } from '@/utils/utmTracking';

/**
 * @param kindeUserId The new user's `kinde_user_id`, forwarded as the Meta dedup
 *   `event_id`. The caller derives it from the access token (see `getUser` in
 *   `@/lib/utils`) — the same claim the backend `sign_up_successful` reads — so
 *   both halves emit the SAME `event_id` and Meta can dedupe the browser event
 *   against the server event.
 */
export function trackSignupConversion(kindeUserId?: string): void {
  if (!IS_SIGNUP_CONVERSION_EVENT_ENABLED()) {
    return;
  }

  const attribution = getUtmParams();
  const landingUrl = getLandingUrl();

  // Single payload sent identically to Segment (→ Meta CAPI) and the GTM
  // dataLayer, so parity is structural: every attribute Segment gets, GTM gets.
  const conversionProps = {
    // Cookie-aware source: this fires on the first post-OAuth get-or-create,
    // when the redux-persist snapshot can still be empty/stale, but the
    // auth-provider cookie is already current. `getPersistedAuthProvider()`
    // would send Auth0 signups with provider undefined/stale here.
    provider: mapAuthProviderToProvider(getEffectiveAuthProvider() ?? undefined),
    // Meta browser↔server dedup key (= kinde_user_id).
    event_id: kindeUserId,
    // UTMs + every ad-network click ID (gclid/gbraid/wbraid/fbclid/msclkid/…).
    ...attribution,
    // Meta Pixel cookie ids (fbp/fbc) for Advanced Matching.
    ...getMetaCookieIds(attribution.fbclid),
    custom_utm_source: attribution.utm_source ?? null,
    landing_url: landingUrl,
  };

  track({
    event_name: TrackingEventName.SignupConversion,
    props: conversionProps,
    // Preserve the attribution-rich landing URL as the event's page URL (the
    // auth-callback URL has none) so Segment/Meta attribute correctly.
    pageUrl: landingUrl,
    options: { segment: true, amplitude: false },
  });

  // Additive GTM signal: lazy-load the container if the funnel gate skipped it,
  // then push the identical payload so GTM-managed tags (Google Ads / Meta) fire.
  pushGtmDataLayerEvent({ event: 'signup_conversion', ...conversionProps });
}
